import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { RemovaCharEspeciais } from '@/utils/format'
import { useUtils } from '@/lib/hooks/utils'
import { useCreateTenant } from '@/lib/hooks/tenant'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { useToast } from '@/lib/hooks/use-toast'

const schema = z.object({
  email: z.string().email('E-mail inválido'),
  whatsapp: z.string().min(1, 'WhatsApp é obrigatório'),
  cnpj: z.string().min(14, 'CNPJ deve ter pelo menos 14 caracteres'),
  razao_social: z.string().min(1, 'Razão social é obrigatória'),
  bairro: z.string().min(1, 'Bairro é obrigatório'),
  numero: z.string().min(1, 'Número é obrigatório'),
  cidade: z.string().min(1, 'Cidade é obrigatória'),
  uf: z.string().min(2, 'UF deve ter 2 caracteres').max(2, 'UF deve ter 2 caracteres'),
  complemento: z.string().optional(),
  logradouro: z.string().min(1, 'Logradouro é obrigatório'),
  comissao: z.string().min(1, 'Comissão é obrigatória'),
  tel: z.string().min(1, 'Telefone é obrigatório'),
  cnaeCode: z.string().min(1, 'Código CNAE é obrigatório'),
  cnaeText: z.string().min(1, 'Descrição CNAE é obrigatória'),
  cep: z.string().optional(),
  fantasia: z.string().optional(),
  tipo: z.string().optional(),
  concordo: z.boolean().refine(val => val === true, 'Você deve concordar com os termos')
})

type FormData = z.infer<typeof schema>

export default function CreateTenantForm() {
  const { buscarCNPJ } = useUtils()
  const { mutateAsync: createTenant } = useCreateTenant()
  const { toast } = useToast()

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      whatsapp: '',
      cnpj: '',
      razao_social: '',
      bairro: '',
      numero: '',
      cidade: '',
      uf: '',
      complemento: '',
      logradouro: '',
      comissao: '',
      tel: '',
      cnaeCode: '',
      cnaeText: '',
      cep: '',
      fantasia: '',
      tipo: '',
      concordo: false
    }
  })

  const handleCnpjChange = async (value: string) => {
    form.setValue('cnpj', value)

    try {
      const newCNPJ = RemovaCharEspeciais(value)
      const response = await buscarCNPJ(newCNPJ)
      if (!response) throw new Error('Nenhuma informação encontrada para este CNPJ.')
      const atividadePrincipal = response.atividade_principal[0]

      form.setValue('tipo', response.tipo || '')
      form.setValue('razao_social', response.nome || '')
      form.setValue('bairro', response.bairro || '')
      form.setValue('numero', response.numero || '')
      form.setValue('cidade', response.municipio || '')
      form.setValue('uf', response.uf || '')
      form.setValue('complemento', response.complemento || '')
      form.setValue('logradouro', response.logradouro || '')
      form.setValue('cep', response.cep || '')
      form.setValue('cnaeCode', atividadePrincipal.code || '')
      form.setValue('cnaeText', atividadePrincipal.text || '')
      form.setValue('fantasia', response.fantasia || '')
      toast({
        description: `CNPJ ${newCNPJ} encontrado com sucesso!`
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        description: err instanceof Error ? err.message : 'Erro ao buscar informações do CNPJ.'
      })
      resetFormFields()
    }
  }

  const resetFormFields = () => {
    form.setValue('razao_social', '')
    form.setValue('bairro', '')
    form.setValue('numero', '')
    form.setValue('cidade', '')
    form.setValue('uf', '')
    form.setValue('complemento', '')
    form.setValue('logradouro', '')
    form.setValue('cep', '')
    form.setValue('cnaeCode', '')
    form.setValue('cnaeText', '')
    form.setValue('fantasia', '')
    form.setValue('tipo', '')
  }

  const onSubmit = async (data: FormData) => {
    try {
      await createTenant({
        cnpj_data: {
          cnpj: await RemovaCharEspeciais(data.cnpj),
          fantasia: data.fantasia || '',
          razao_social: data.razao_social,
          logradouro: data.logradouro,
          bairro: data.bairro,
          numero: data.numero,
          cidade: data.cidade,
          uf: data.uf,
          complemento: data.complemento || '',
          cnae: data.cnaeCode,
          cnae_info: data.cnaeText,
          tipo: data.tipo || '',
          cep: data.cep || ''
        },
        info_tenant: {
          email: data.email,
          whatsapp: data.whatsapp
        },
        Regime_produto: {
          comissao: parseFloat(data.comissao),
          concordo: data.concordo
        }
      })
      toast({ description: 'Tenant criado com sucesso!' })
      form.reset()
    } catch (error: any) {
      toast({ variant: 'destructive', description: error.message })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Dados Pessoais */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Dados de Contato</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Digite o e-mail" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="whatsapp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o WhatsApp" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Dados da Empresa */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Dados da Empresa</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNPJ</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Digite o CNPJ"
                      {...field}
                      onChange={e => handleCnpjChange(e.target.value)}
                      className="bg-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="razao_social"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razão Social</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite a razão social" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cnaeCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código CNAE</FormLabel>
                  <FormControl>
                    <Input placeholder="Código CNAE" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cnaeText"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição CNAE</FormLabel>
                  <FormControl>
                    <Input placeholder="Descrição do CNAE" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Endereço */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Endereço</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="bairro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bairro</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o bairro" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="numero"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o número" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cidade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cidade</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite a cidade" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="uf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>UF</FormLabel>
                  <FormControl>
                    <Input placeholder="UF" maxLength={2} {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="complemento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Complemento</FormLabel>
                  <FormControl>
                    <Input placeholder="Complemento (opcional)" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="logradouro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logradouro</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o logradouro" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Outros Dados */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Outros Dados</h3>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="comissao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comissão (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Digite a comissão"
                      {...field}
                      className="bg-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o telefone" {...field} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Termos e Condições */}
        <FormField
          control={form.control}
          name="concordo"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Li e concordo com os termos</FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        {/* Botão de Ação */}
        <div className="flex justify-between mt-6">
          <Button
            type="submit"
            className="bg-primary hover:bg-primary-hover text-primary-foreground"
            disabled={!form.watch('concordo')}
          >
            Criar Tenant
          </Button>
        </div>
      </form>
    </Form>
  )
}
